import API_BASE from '../config';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';

export default function AdminWebhooks() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | active | inactive | disabled
  const [search, setSearch] = useState('');
  const [deliveriesFor, setDeliveriesFor] = useState(null);
  const [deliveries, setDeliveries] = useState([]);

  async function authFetch(url, opts = {}) {
    const token = await getToken();
    return fetch(url, { ...opts, headers: { ...(opts.headers || {}), Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' } });
  }

  async function load() {
    setLoading(true);
    try {
      const res = await authFetch(API_BASE + '/admin/webhooks');
      if (!res.ok) throw new Error('Failed to load');
      setRows(await res.json());
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }
  async function loadDeliveries(wid) {
    try {
      const res = await authFetch(API_BASE + '/webhooks/' + wid + '/deliveries');
      if (res.ok) setDeliveries(await res.json());
    } catch (e) { toast.error('Failed to load deliveries'); }
  }
  async function enableWebhook(wid) {
    try {
      const res = await authFetch(API_BASE + '/webhooks/' + wid + '/enable', { method: 'POST', body: JSON.stringify({}) });
      if (!res.ok) throw new Error('Enable failed');
      toast.success('Re-enabled');
      await load();
    } catch (e) { toast.error(e.message); }
  }
  async function deleteWebhook(wid) {
    if (!window.confirm('Delete this webhook? Deliveries history will be removed.')) return;
    try {
      const res = await authFetch(API_BASE + '/webhooks/' + wid, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Deleted');
      await load();
    } catch (e) { toast.error(e.message); }
  }
  async function replayDelivery(did) {
    try {
      const res = await authFetch(API_BASE + '/deliveries/' + did + '/replay', { method: 'POST', body: JSON.stringify({}) });
      if (!res.ok) throw new Error('Replay failed');
      toast.success('Replayed');
      if (deliveriesFor) await loadDeliveries(deliveriesFor.id);
    } catch (e) { toast.error(e.message); }
  }

  useEffect(() => { load(); }, []);

  const filtered = rows.filter(r => {
    if (filter === 'active' && (!r.active || r.disabled_at)) return false;
    if (filter === 'inactive' && r.active) return false;
    if (filter === 'disabled' && !r.disabled_at) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(r.url || '').toLowerCase().includes(q) && !(r.client_name || '').toLowerCase().includes(q) && !(r.description || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const summary = {
    total: rows.length,
    active: rows.filter(r => r.active && !r.disabled_at).length,
    disabled: rows.filter(r => r.disabled_at).length,
    totalDeliveries: rows.reduce((a, r) => a + Number(r.total_deliveries || 0), 0),
    failed: rows.reduce((a, r) => a + Number(r.failed_deliveries || 0), 0),
  };

  return (
    <div style={{ backgroundColor: '#FAFAF8', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <div style={{ padding: '1.5rem 2rem', maxWidth: '1200px', margin: '0 auto' }}>
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.83rem', cursor: 'pointer', padding: 0, marginBottom: '1.5rem' }}>← Back</button>
        <h1 style={{ fontSize: '1.4rem', fontWeight: 500, margin: '0 0 0.25rem', color: '#1A1A1A' }}>Webhooks · Admin</h1>
        <p style={{ margin: '0 0 1.5rem', fontSize: '0.85rem', color: '#888' }}>Every outbound webhook across every client. Failed deliveries are tracked here.</p>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
          <SummaryCard label="Total" value={summary.total} />
          <SummaryCard label="Active" value={summary.active} accent="#276749" />
          <SummaryCard label="Auto-disabled" value={summary.disabled} accent="#C53030" />
          <SummaryCard label="Deliveries (all-time)" value={summary.totalDeliveries.toLocaleString()} />
          <SummaryCard label="Failed deliveries" value={summary.failed.toLocaleString()} accent="#C53030" />
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search URL, client, description…" style={{ flex: 1, minWidth: '220px', fontSize: '0.85rem', border: '0.5px solid #D4CFC9', borderRadius: '6px', padding: '0.45rem 0.7rem', outline: 'none' }} />
          {['all','active','inactive','disabled'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ fontSize: '0.78rem', padding: '0.35rem 0.75rem', borderRadius: '6px', border: '0.5px solid ' + (filter === f ? '#1A1A1A' : '#D4CFC9'), background: filter === f ? '#1A1A1A' : '#fff', color: filter === f ? '#fff' : '#1A1A1A', cursor: 'pointer', textTransform: 'capitalize' }}>{f}</button>
          ))}
        </div>

        {loading ? (
          <p style={{ color: '#888' }}>Loading…</p>
        ) : filtered.length === 0 ? (
          <p style={{ color: '#888' }}>No webhooks match.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {filtered.map(r => (
              <div key={r.id} style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '0.9rem 1.1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.7rem', color: r.disabled_at ? '#C53030' : (r.active ? '#276749' : '#888'), background: r.disabled_at ? '#FED7D7' : (r.active ? '#C6F6D5' : '#EDF2F7'), padding: '0.15rem 0.5rem', borderRadius: '4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {r.disabled_at ? 'Auto-disabled' : (r.active ? 'Active' : 'Inactive')}
                      </span>
                      <span style={{ fontSize: '0.85rem', color: '#1A1A1A', fontWeight: 500 }}>{r.client_name || '—'}</span>
                      {r.description && <span style={{ fontSize: '0.78rem', color: '#888' }}>· {r.description}</span>}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#1A1A1A', fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{r.url}</div>
                    <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '0.3rem' }}>
                      Events: {(r.event_types || []).join(', ') || '—'} · Deliveries: {r.total_deliveries || 0} · Failed: {r.failed_deliveries || 0} · Last: {r.last_delivery_at ? new Date(r.last_delivery_at).toLocaleString() : 'never'}
                    </div>
                    {r.disabled_at && (
                      <div style={{ fontSize: '0.72rem', color: '#C53030', marginTop: '0.25rem' }}>Reason: {r.disabled_reason || 'consecutive failures'}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', flexShrink: 0 }}>
                    <button onClick={() => { setDeliveriesFor(r); loadDeliveries(r.id); }} style={btn}>Deliveries</button>
                    <button onClick={() => navigate('/clients/' + r.client_id + '/webhooks')} style={btn}>Open in client</button>
                    {r.disabled_at && <button onClick={() => enableWebhook(r.id)} style={{ ...btn, color: '#276749', borderColor: '#9AE6B4' }}>Re-enable</button>}
                    <button onClick={() => deleteWebhook(r.id)} style={{ ...btn, color: '#C53030', borderColor: '#FED7D7' }}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {deliveriesFor && (
        <div style={modalBackdrop} onClick={() => setDeliveriesFor(null)}>
          <div style={modalBody} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem' }}>Deliveries · {deliveriesFor.client_name}</h2>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.78rem', color: '#888', fontFamily: 'monospace' }}>{deliveriesFor.url}</p>
            <div style={{ maxHeight: '480px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {deliveries.length === 0 && <span style={{ color: '#888', fontSize: '0.85rem' }}>No deliveries yet</span>}
              {deliveries.map(d => (
                <div key={d.id} style={{ border: '0.5px solid #E8E4E0', borderRadius: '6px', padding: '0.6rem 0.75rem', fontSize: '0.78rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                    <span style={{ fontFamily: 'monospace' }}>{d.event_type}</span>
                    <span style={{ color: d.status === 'success' ? '#276749' : (d.status === 'failed' ? '#C53030' : '#B7791F') }}>{d.status} · {d.response_status || '—'}</span>
                  </div>
                  <div style={{ color: '#888', fontSize: '0.72rem', marginTop: '0.2rem' }}>
                    Attempt {d.attempt}/{d.max_attempts} · {d.created_at ? new Date(d.created_at).toLocaleString() : ''}
                  </div>
                  {d.error_message && <div style={{ color: '#C53030', fontSize: '0.72rem', marginTop: '0.2rem' }}>{d.error_message}</div>}
                  <div style={{ marginTop: '0.35rem' }}>
                    <button onClick={() => replayDelivery(d.id)} style={{ ...btn, fontSize: '0.72rem', padding: '0.25rem 0.6rem' }}>Replay</button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
              <button onClick={() => setDeliveriesFor(null)} style={btn}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, accent }) {
  return (
    <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '8px', padding: '0.7rem 1rem', minWidth: '120px' }}>
      <div style={{ fontSize: '0.68rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ fontSize: '1.2rem', fontWeight: 500, color: accent || '#1A1A1A' }}>{value}</div>
    </div>
  );
}

const btn = { fontSize: '0.78rem', color: '#1A1A1A', background: '#fff', border: '0.5px solid #D4CFC9', borderRadius: '6px', padding: '0.35rem 0.7rem', cursor: 'pointer' };
const modalBackdrop = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalBody = { background: '#fff', borderRadius: '12px', padding: '1.5rem', maxWidth: '720px', width: '90%', maxHeight: '90vh', overflowY: 'auto' };
